import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { trackAdAddToCart } from '@/lib/analytics';

export interface Listing {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  supplier: string;
  location: string;
  availableQuantity: number;
  slug?: string; // Optional slug for SEO-friendly URLs
  group_buy_enabled?: boolean; // Is this a group buy product
  liquidation_sale_enabled?: boolean; // Is this a liquidation sale product
  grade?: 'A' | 'B' | 'C' | 'D';
}

export interface CartItem {
  id: string;
  product: Listing;
  quantity: number; // For package products: number of packages. For others: actual quantity
  unitPrice: number;
  moq: number; // Minimum Order Quantity (in sqm for package products)
  stepQuantity?: number; // Step quantity for increments (in sqm for package products)
  maxOrderQuantity?: number; // Maximum quantity per order (in sqm for package products)
  isPackageProduct?: boolean; // Is this a package-based product
  packageCoverage?: number; // Coverage per package (e.g., 1.15 m²)
  measurementUnit?: string; // Measurement unit
  requestedQuantity?: number; // For package products: user's desired sqm (e.g., 15, 20, 25)
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalPrice: number;
  selectedItems: Set<string>;
  isCheckoutActive: boolean;
}

interface CartContextType {
  // State
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalPrice: number;
  selectedItems: Set<string>;
  isCheckoutActive: boolean;

  // Actions
  addItem: (
    product: Listing,
    quantity: number,
    moq?: number,
    stepQuantity?: number,
    maxOrderQuantity?: number,
    isPackageProduct?: boolean,
    packageCoverage?: number,
    measurementUnit?: string,
    requestedQuantity?: number,
    overwrite?: boolean
  ) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  incrementQuantity: (id: string) => void;
  decrementQuantity: (id: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  navigateToCart: () => void;
  toggleItemSelection: (id: string) => void;
  setSelectedItems: (ids: Set<string>) => void;
  selectAllItems: () => void;
  getSelectedItemsCount: () => number;
  getSelectedItemsTotal: () => number;
  setCheckoutActive: (active: boolean) => void;
  clearCheckoutState: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, setState] = useState<CartState>({
    items: [],
    isOpen: false,
    totalItems: 0,
    totalPrice: 0,
    selectedItems: new Set<string>(),
    isCheckoutActive: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load cart from localStorage on initialization
  useEffect(() => {
    const loadCart = async () => {
      try {
        // Add small delay to prevent hydration issues
        await new Promise(resolve => setTimeout(resolve, 200));

        const savedCart = localStorage.getItem('materialflow-cart');
        const savedSelected = localStorage.getItem('selectedCartItems');

        if (savedCart) {
          const cartItems: CartItem[] = JSON.parse(savedCart);
          const selectedIds = savedSelected ? new Set<string>(JSON.parse(savedSelected)) : new Set<string>();

          setState(prev => ({
            ...prev,
            items: cartItems,
            totalItems: cartItems.reduce((sum, item) => sum + item.quantity, 0),
            totalPrice: cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0),
            selectedItems: selectedIds,
            isCheckoutActive: false, // Reset checkout state on load
          }));
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        // Reset to empty cart on error
        setState(prev => ({
          ...prev,
          items: [],
          totalItems: 0,
          totalPrice: 0,
          selectedItems: new Set<string>(),
          isCheckoutActive: false,
        }));
      } finally {
        setIsLoading(false);
      }
    };

    // Make cart context available globally for breadcrumb access and logout handling
    window.cartContextRef = {
      openCart: () => setState(prev => ({ ...prev, isOpen: true })),
      clearCheckoutState: () => setState(prev => ({ ...prev, isCheckoutActive: false })),
      navigateToCart: () => {
        if (window.location.pathname !== '/cart') {
          window.location.href = '/cart';
        }
      }
    };

    loadCart();
  }, []);

  // Function to validate cart items against active products
  const validateCartItems = async () => {
    if (state.items.length === 0) return;

    try {
      // Use the public product function instead of direct table access
      const { data: activeProducts, error } = await supabase
        .rpc('get_public_product_catalog');

      if (error) {
        console.error('Error validating cart items:', error);
        return;
      }

      const activeProductIds = new Set(
        activeProducts?.map(p => p.id) || []
      );

      const inactiveItems = state.items.filter(item => !activeProductIds.has(item.id));

      if (inactiveItems.length > 0) {
        // Remove inactive items from cart
        setState(prev => {
          const newItems = prev.items.filter(item => activeProductIds.has(item.id));
          const newSelectedItems = new Set(
            Array.from(prev.selectedItems).filter(id => activeProductIds.has(id))
          );

          const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

          return {
            ...prev,
            items: newItems,
            totalItems: newTotalItems,
            totalPrice: newTotalPrice,
            selectedItems: newSelectedItems,
          };
        });

        // Notify user about removed items
        const removedNames = inactiveItems.map(item => item.product.name).join(', ');
        toast.error("מוצרים הוסרו מהעגלה", {
          description: `המוצרים הבאים הוסרו מהעגלה מכיוון שהם לא זמינים יותר: ${removedNames}`
        });
      }
    } catch (error) {
      console.error('Error in cart validation:', error);
    }
  };

  // Validate cart items when component mounts and periodically
  useEffect(() => {
    if (!isLoading && state.items.length > 0) {
      validateCartItems();

      // Set up periodic validation every 5 minutes
      const interval = setInterval(validateCartItems, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isLoading, state.items.length]); // Only depend on loading state and items count

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    // Don't save during initial loading
    if (isLoading) return;

    try {
      localStorage.setItem('materialflow-cart', JSON.stringify(state.items));
      localStorage.setItem('selectedCartItems', JSON.stringify(Array.from(state.selectedItems)));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [state.items, state.selectedItems, isLoading]);

  const addItem = (
    product: Listing,
    quantity: number,
    moq: number = 1,
    stepQuantity: number = 1,
    maxOrderQuantity?: number,
    isPackageProduct: boolean = false,
    packageCoverage?: number,
    measurementUnit?: string,
    requestedQuantity?: number, // For package products: user's desired sqm
    overwrite: boolean = false
  ) => {
    setState(prev => {
      const existingItemIndex = prev.items.findIndex(item => item.id === product.id);
      let newItems: CartItem[];
      let newSelectedItems = new Set(prev.selectedItems);

      // --- VALIDATION LOGIC START ---
      // --- VALIDATION LOGIC START ---
      const existingQty = existingItemIndex >= 0 ? prev.items[existingItemIndex].quantity : 0;

      // Fix: If overwriting (updating), the new total is just 'quantity'. If adding, it's 'existing + quantity'.
      const projectedQty = overwrite ? quantity : (existingQty + quantity);

      // Determine the effective maximum quantity
      // For Group Buy, we might trust maxOrderQuantity if provided, else huge number
      // For others, min of maxOrderQuantity (if set) and availableQuantity
      const isGroupBuy = product.group_buy_enabled;

      let effectiveMax = product.availableQuantity || 999999;
      if (maxOrderQuantity) {
        effectiveMax = isGroupBuy ? maxOrderQuantity : Math.min(maxOrderQuantity, effectiveMax);
      } else if (isGroupBuy) {
        effectiveMax = 999999; // No limit if not specified for Group Buy
      }

      // Validating...
      let quantityToAdd = quantity;

      if (projectedQty > effectiveMax) {
        const allowed = overwrite ? effectiveMax : Math.max(0, effectiveMax - existingQty);

        if (allowed === 0 && !overwrite) {
          toast.error("הגעת לכמות המקסימלית", { description: `לא ניתן להוסיף עוד יחידות למוצר זה. המקסימום הוא ${effectiveMax}.` });
          return prev;
        } else if (overwrite && quantity > effectiveMax) {
          toast.warning("כמות הוגבלה", { description: `הכמות עודכנה למקסימום האפשרי: ${effectiveMax}.` });
          quantityToAdd = effectiveMax;
        } else if (!overwrite) {
          toast.warning("כמות הוגבלה", { description: `נוספו רק ${allowed} יחידות כדי לא לחרוג מהמקסימום של ${effectiveMax}.` });
          quantityToAdd = allowed;
        }
      } else {
        if (overwrite) {
          quantityToAdd = quantity;
        }
      }
      // --- VALIDATION LOGIC END ---

      if (existingItemIndex >= 0) {
        // Update existing item with new values
        newItems = [...prev.items];
        const existingItem = newItems[existingItemIndex];

        // Calculate final quantity based on overwrite flag
        let finalQty = overwrite ? quantityToAdd : (existingQty + quantityToAdd);

        // Security check - should have been caught by validation above but safe to double check
        if (finalQty > effectiveMax) {
          finalQty = effectiveMax;
        }

        // Apply changes
        newItems[existingItemIndex] = {
          ...existingItem,
          product, // Update product details with latest data
          quantity: finalQty,
          requestedQuantity: (requestedQuantity !== undefined) ? requestedQuantity : existingItem.requestedQuantity,
        };

        // Toast removed as per request - Cart opens automatically
        // if (overwrite) {
        //   toast.success("כמות עודכנה", { description: `${product.name} עודכן בעגלה ל-${finalQty} יחידות.` });
        // }
        // Toast removed as per request - Cart opens automatically
        // else {
        //   toast.success("כמות עודכנה", { description: `${product.name} עודכן בעגלה` });
        // }
      } else {
        // Add new item

        // Validation for NEW item
        if (quantity > effectiveMax) {
          toast.warning("כמות הוגבלה", { description: `הכמות הוגבלה למקסימום האפשרי: ${effectiveMax}.` });
          quantity = effectiveMax;
        }

        const newItem: CartItem = {
          id: product.id,
          product,
          quantity,
          unitPrice: product.price,
          moq,
          stepQuantity,
          maxOrderQuantity,
          isPackageProduct,
          packageCoverage,
          measurementUnit,
          requestedQuantity: isPackageProduct ? requestedQuantity : undefined,
        };
        newItems = [...prev.items, newItem];
        // Auto-select new items
        newSelectedItems.add(product.id);

        // Toast removed as per request - Cart opens automatically
        // toast.success("נוסף לעגלה!", { description: `${product.name} נוסף לעגלה בהצלחה` });

        // Track add to cart event
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'add_to_cart', {
            currency: 'ILS',
            value: product.price * quantity,
            items: [{
              item_id: product.id,
              item_name: product.name,
              price: product.price,
              quantity: quantity
            }]
          });
        }
        
        // Track ad pixel add to cart
        trackAdAddToCart(product.price * quantity, product.id, quantity);
      }

      const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

      const newState = {
        ...prev,
        items: newItems,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice,
        selectedItems: newSelectedItems,
        isOpen: true, // Automatically open cart when item is added
      };

      return newState;
    });
  };

  const removeItem = (id: string) => {
    setState(prev => {
      const itemToRemove = prev.items.find(item => item.id === id);

      // Track remove from cart event
      if (itemToRemove && typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'remove_from_cart', {
          currency: 'ILS',
          value: itemToRemove.unitPrice * itemToRemove.quantity,
          items: [{
            item_id: itemToRemove.id,
            item_name: itemToRemove.product.name,
            price: itemToRemove.unitPrice,
            quantity: itemToRemove.quantity
          }]
        });
      }

      const newItems = prev.items.filter(item => item.id !== id);
      const newSelectedItems = new Set(prev.selectedItems);
      newSelectedItems.delete(id);

      const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

      return {
        ...prev,
        items: newItems,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice,
        selectedItems: newSelectedItems,
      };
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    // Don't allow quantity changes during checkout
    if (state.isCheckoutActive) return;

    setState(prev => {
      const newItems = prev.items.map(item => {
        if (item.id === id) {
          return { ...item, quantity: Math.max(quantity, item.moq) };
        }
        return item;
      });

      const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

      return {
        ...prev,
        items: newItems,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice,
      };
    });
  };

  const incrementQuantity = (id: string) => {
    // Don't allow quantity changes during checkout
    if (state.isCheckoutActive) return;

    setState(prev => {
      const newItems = prev.items.map(item => {
        if (item.id === id) {
          if (item.isPackageProduct && item.packageCoverage) {
            // For package products: work with PACKAGES directly
            const stepQty = item.stepQuantity || 1;
            const newPackages = item.quantity + stepQty;
            const maxQty = item.maxOrderQuantity || item.product.availableQuantity || 999999;

            if (newPackages <= maxQty) {
              const newRequestedQty = Number((newPackages * item.packageCoverage).toFixed(2));
              return { ...item, quantity: newPackages, requestedQuantity: newRequestedQty };
            }
            return item; // Don't change if exceeds max
          } else {
            // Non-package products: regular increment
            const step = item.stepQuantity || 1;
            const isGroupBuy = item.product.group_buy_enabled;
            const newQuantity = item.quantity + step;

            // Calculate max quantity logic similar to CartSidebar
            let maxQty = item.product.availableQuantity;

            if (isGroupBuy) {
              // For Group Buy: Use maxOrderQuantity if set, otherwise assume unlimited (availableQuantity is ignored)
              maxQty = item.maxOrderQuantity || 999999;
            } else {
              // For Regular: Use min of maxOrderQuantity (if set) and availableQuantity
              maxQty = Math.min(
                item.maxOrderQuantity || item.product.availableQuantity,
                item.product.availableQuantity
              );
            }

            if (newQuantity <= maxQty) {
              return { ...item, quantity: newQuantity };
            } else {
              if (isGroupBuy) {
                toast.error("כמות מקסימלית להזמנה", { description: `הכמות המקסימלית למוצר זה היא ${maxQty} יח'.` });
              } else {
                toast.error("אין מספיק במלאי", { description: `המלאי הזמין הוא ${maxQty} יח' בלבד.` });
              }
            }
          }
        }
        return item;
      });

      const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

      return {
        ...prev,
        items: newItems,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice,
      };
    });
  };

  const decrementQuantity = (id: string) => {
    // Don't allow quantity changes during checkout
    if (state.isCheckoutActive) return;

    setState(prev => {
      const newItems = prev.items.map(item => {
        if (item.id === id) {
          if (item.isPackageProduct && item.packageCoverage) {
            // For package products: work with PACKAGES directly
            const stepQty = item.stepQuantity || 1;
            const minQty = item.moq || 1; // moq is now PACKAGES
            const newPackages = item.quantity - stepQty;

            if (newPackages >= minQty) {
              const newRequestedQty = Number((newPackages * item.packageCoverage).toFixed(2));
              return { ...item, quantity: newPackages, requestedQuantity: newRequestedQty };
            }
            return item; // Don't change if below min
          } else {
            // Non-package products: regular decrement
            const step = item.stepQuantity || 1;
            const newQuantity = item.quantity - step;

            if (newQuantity >= item.moq) {
              return { ...item, quantity: newQuantity };
            }
          }
        }
        return item;
      });

      const newTotalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
      const newTotalPrice = newItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

      return {
        ...prev,
        items: newItems,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice,
      };
    });
  };

  const clearCart = () => {
    setState(prev => ({
      ...prev,
      items: [],
      totalItems: 0,
      totalPrice: 0,
      selectedItems: new Set<string>(),
      isCheckoutActive: false,
    }));
  };

  const setCheckoutActive = (active: boolean) => {
    setState(prev => ({
      ...prev,
      isCheckoutActive: active,
      isOpen: active ? false : prev.isOpen // Close cart when checkout starts
    }));
  };

  const clearCheckoutState = () => {
    setState(prev => ({ ...prev, isCheckoutActive: false }));
  };

  const toggleCart = () => {
    // Don't allow cart to open if checkout is active
    setState(prev => ({
      ...prev,
      isOpen: prev.isCheckoutActive ? false : !prev.isOpen
    }));
  };

  const openCart = () => {
    setState(prev => {
      // Track view cart event
      if (typeof window !== 'undefined' && window.gtag && prev.items.length > 0) {
        const totalValue = prev.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
        window.gtag('event', 'view_cart', {
          currency: 'ILS',
          value: totalValue,
          items: prev.items.map(item => ({
            item_id: item.id,
            item_name: item.product.name,
            price: item.unitPrice,
            quantity: item.quantity
          }))
        });
      }

      // Clear checkout state when accessing cart
      return {
        ...prev,
        isOpen: true,
        isCheckoutActive: false
      };
    });
  };

  const closeCart = () => {
    setState(prev => ({ ...prev, isOpen: false }));
  };

  const navigateToCart = () => {
    // Navigate to cart page and clear checkout state
    setState(prev => ({
      ...prev,
      isOpen: false,
      isCheckoutActive: false
    }));
    // Navigate to cart page using the global reference
    if (window.cartContextRef?.navigateToCart) {
      window.cartContextRef.navigateToCart();
    }
  };

  const toggleItemSelection = (id: string) => {
    setState(prev => {
      const newSelectedItems = new Set(prev.selectedItems);
      if (newSelectedItems.has(id)) {
        newSelectedItems.delete(id);
      } else {
        newSelectedItems.add(id);
      }
      return { ...prev, selectedItems: newSelectedItems };
    });
  };

  const setSelectedItems = (ids: Set<string>) => {
    setState(prev => ({ ...prev, selectedItems: ids }));
  };

  const selectAllItems = () => {
    setState(prev => {
      const allIds = new Set(prev.items.map(item => item.id));
      return { ...prev, selectedItems: allIds };
    });
  };

  const getSelectedItemsCount = () => {
    return state.items.filter(item => state.selectedItems.has(item.id)).length;
  };

  const getSelectedItemsTotal = () => {
    return state.items
      .filter(item => state.selectedItems.has(item.id))
      .reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  };

  const value: CartContextType = {
    // State
    items: state.items,
    isOpen: state.isOpen,
    totalItems: state.totalItems,
    totalPrice: state.totalPrice,
    selectedItems: state.selectedItems,
    isCheckoutActive: state.isCheckoutActive,

    // Actions
    addItem,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    navigateToCart,
    toggleItemSelection,
    setSelectedItems,
    selectAllItems,
    getSelectedItemsCount,
    getSelectedItemsTotal,
    setCheckoutActive,
    clearCheckoutState,
  };

  // Cart is loaded, no loading screen needed
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};